import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { NewMessageComposerProvider } from './context/NewMessageComposerContext';
import NewMessageComposer from './components/MessagePage/NewMessageComposer';
import UnifiedAuthPage from './components/UnifiedAuthPage';
import OAuthCallbackPage from './components/OAuthCallbackPage';
import ProfilePage from './components/ProfilePage';
import UserProfilePage from './components/ProfilePage/UserProfilePage';
import HomePage from './components/HomePage';
import SettingsPage from './components/SettingsPage';
import WalletPage from './components/WalletPage';
import SubscriptionsPage from './components/SubscriptionsPage';
import SupportPage from './components/SupportPage';
import ReportHistoryPage from './components/ReportHistoryPage';
import MessagePage from './components/MessagePage';
import GoLivePage from './components/GoLivePage';
import LegalPage from './components/LegalPage';
import UploadPage from './components/UploadPage';
import AdminDashboard, {
  OverviewPage as AdminOverviewPage,
  RevenuePage as AdminRevenuePage,
  UsersPage as AdminUsersPage,
  ModerationPage as AdminModerationPage,
  SupportTicketsPage as AdminSupportTicketsPage,
  ComingSoonPage as AdminComingSoonPage,
} from './components/AdminDashboard';
import CommunityPage from './components/CommunityPage';
import { ROUTES } from './constants';

/**
 * Root application component with routing configuration.
 *
 * Route hierarchy:
 * - / → UnifiedAuthPage (landing + auth, first page users see)
 * - /auth → redirect to / for bookmarked auth links
 * - /auth/callback → OAuthCallbackPage (handles backend OAuth redirect)
 * - /login, /signup, /forgot-password → Redirect to /
 * - /home → Home Dashboard (main experience after login)
 * - /profile → User profile setup
 * - /settings → Account settings
 * - /wallet → Transaction history
 * - /support → Help and support form
 * - /reports → Report history
 * - /messages → Messaging interface
 *
 * @returns {JSX.Element}
 */
function App() {
  return (
    <BrowserRouter>
      <NewMessageComposerProvider>
      <NewMessageComposer />
      <Routes>
        {/* Landing + Auth — first page users see */}
        <Route path={ROUTES.WELCOME} element={<UnifiedAuthPage />} />

        {/* /auth redirects to / for any bookmarked links */}
        <Route path={ROUTES.AUTH} element={<Navigate to={ROUTES.WELCOME} replace />} />

        {/* OAuth Callback — handles redirect from backend after OAuth */}
        <Route path={ROUTES.AUTH_CALLBACK} element={<OAuthCallbackPage />} />

        {/* Legacy redirects */}
        <Route path={ROUTES.LOGIN} element={<Navigate to={ROUTES.WELCOME} replace />} />
        <Route path={ROUTES.SIGNUP} element={<Navigate to={ROUTES.WELCOME} replace />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<Navigate to={ROUTES.WELCOME} replace />} />

        {/* Home Dashboard — main experience after login */}
        <Route path={ROUTES.HOME} element={<HomePage />} />

        {/* Go Live — streaming dashboard */}
        <Route path={ROUTES.GO_LIVE} element={<GoLivePage />} />

        {/* Upload — media library and ad asset uploads */}
        <Route path={ROUTES.UPLOAD} element={<UploadPage />} />

        {/* Admin — nested dashboard */}
        <Route path={ROUTES.ADMIN} element={<AdminDashboard />}>
          <Route index element={<AdminOverviewPage />} />
          <Route path="revenue" element={<AdminRevenuePage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="moderation" element={<AdminModerationPage />} />
          <Route path="support" element={<AdminSupportTicketsPage />} />
          <Route
            path="streams"
            element={<AdminComingSoonPage title="Streams" description="Live stream activity and historical analytics." />}
          />
          <Route
            path="payouts"
            element={<AdminComingSoonPage title="Payouts" description="Streamer earnings and payout queue." />}
          />
          <Route
            path="reports"
            element={<AdminComingSoonPage title="Reports" description="User-submitted reports of other users or content." />}
          />
          <Route
            path="settings"
            element={<AdminComingSoonPage title="Settings" description="Platform configuration: fee %, spade rates, feature flags." />}
          />
        </Route>

        {/* Profile setup (accessible from user dropdown) */}
        <Route path={ROUTES.PROFILE} element={<ProfilePage />} />
        <Route path={ROUTES.PUBLIC_PROFILE} element={<UserProfilePage />} />

        {/* Account settings */}
        <Route path={ROUTES.SETTINGS} element={<SettingsPage />} />

        {/* Wallet — transaction history */}
        <Route path={ROUTES.WALLET} element={<WalletPage />} />

        {/* Subscriptions — viewer→streamer 30-day subscriptions */}
        <Route path={ROUTES.SUBSCRIPTIONS} element={<SubscriptionsPage />} />

        {/* Support — help form */}
        <Route path={ROUTES.SUPPORT} element={<SupportPage />} />
        <Route path={ROUTES.CONTACT} element={<SupportPage />} />

        {/* Report History */}
        <Route path={ROUTES.REPORTS} element={<ReportHistoryPage />} />

        {/* Legal pages */}
        <Route path={ROUTES.TERMS} element={<LegalPage />} />
        <Route path={ROUTES.PRIVACY} element={<LegalPage />} />
        <Route path={ROUTES.GUIDELINES} element={<LegalPage />} />

        {/* Messages */}
        <Route path={ROUTES.MESSAGES} element={<MessagePage />} />

        {/* Community — IVS Chat */}
        <Route path={ROUTES.COMMUNITY} element={<CommunityPage />} />
        <Route
          path={ROUTES.COMMUNITY_USER}
          element={
            String(process.env.REACT_APP_ENABLE_PER_USER_CHATS).toLowerCase() === 'true'
              ? <CommunityPage />
              : <Navigate to={ROUTES.COMMUNITY} replace />
          }
        />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to={ROUTES.WELCOME} replace />} />
      </Routes>
      </NewMessageComposerProvider>
    </BrowserRouter>
  );
}

export default App;
